import api, { APIError } from "api";
import { supportApiBase } from "api/constants";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { RolloutStatus } from "routes/healthForms/api";
import { queryKeys } from "utils/misc";

export type EnvGroup = "stage" | "prod";

export type CatalogueKind = "exercise" | "lesson" | "template";

export type CatalogueGroupRollout = {
  group: EnvGroup;
  deployable: boolean;
  status: RolloutStatus;
  has_undeployed_changes: boolean;
  deployed_at: string | null;
  deployed_by_email: string | null;
  confirmed_at: string | null;
};

export type CatalogueRollout = {
  kind: CatalogueKind;
  name: string;
  groups: CatalogueGroupRollout[];
};

const ROLLOUT_POLL_INTERVAL = 30_000;

export const catalogueRolloutsKey = ["catalogue-rollouts"] as const;

const hasPendingRollout = (rollouts?: CatalogueRollout[]) =>
  rollouts?.some(rollout =>
    rollout.groups.some(group => group.status === "activating" || group.status === "retiring")
  ) ?? false;

export const useGetCatalogueRollouts = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery<CatalogueRollout[]>(
    catalogueRolloutsKey,
    async () => {
      // @ts-ignore — v2 endpoint, not in OpenAPI types
      const data = await api.parse("GET /catalogue/deployments", { root: supportApiBase });
      return Array.isArray(data) ? data : [];
    },
    {
      enabled,
      retry: false,
      refetchInterval: (data?: CatalogueRollout[]) => (hasPendingRollout(data) ? ROLLOUT_POLL_INTERVAL : false),
    }
  );

export const rolloutFor = (rollouts: CatalogueRollout[] | undefined, kind: CatalogueKind, name: string) =>
  rollouts?.find(rollout => rollout.kind === kind && rollout.name === name);

type CatalogueTarget = { kind: CatalogueKind; name: string; group: EnvGroup };

const invalidateDeployed = (queryClient: ReturnType<typeof useQueryClient>, { kind, name, group }: CatalogueTarget) => {
  queryClient.invalidateQueries(catalogueRolloutsKey);
  queryClient.invalidateQueries(deployedCatalogueEntryKey(kind, name, group));
};

export const useDeployCatalogueEntry = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ kind, name, group }: CatalogueTarget): Promise<void> => {
      await api.call(`/catalogue/${kind}/${encodeURIComponent(name)}/deployments`, {
        root: supportApiBase,
        method: "POST",
        data: { group },
      });
    },
    { onSuccess: (_result, target) => invalidateDeployed(queryClient, target) }
  );
};

export const useRetireCatalogueEntry = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ kind, name, group }: CatalogueTarget): Promise<void> => {
      await api.call(`/catalogue/${kind}/${encodeURIComponent(name)}/deployments/${group}`, {
        root: supportApiBase,
        method: "DELETE",
      });
    },
    { onSuccess: (_result, target) => invalidateDeployed(queryClient, target) }
  );
};

export type DeployedExercise = {
  name: string;
  duration_minutes: number | null;
  target_joints: string[];
  primary_function: string;
  premium: boolean;
  state: string;
  retired: boolean;
  levels: {
    level: number;
    sets: number;
    reps: string;
    equipment: string[];
    duration_unit: string | null;
    duration_icon: string | null;
    movement: string | null;
    position: string | null;
    deprecated: boolean;
    translations: {
      locale: string;
      title: string;
      about: string | null;
      variation_title: string | null;
      tip_title: string | null;
      tip_description: string | null;
      instructions_icon_name: string;
      instructions_text1: string | null;
      instructions_text2: string;
      instructions_text3: string | null;
      image_url: string | null;
      video_url: string | null;
      video_file_url: string | null;
      video_thumbnail_file_url: string | null;
      caption_file_url: string | null;
      extra_swedish_description: string | null;
    }[];
  }[];
};

export type DeployedLesson = {
  name: string;
  target_joints: string[];
  state: string;
  retired: boolean;
  parts: {
    order: number;
    deprecated: boolean;
    questions: { order: number; correct_alternative: number }[];
    translations: {
      locale: string;
      title: string;
      content: { type: string; content: string }[];
      questions: { title: string; tip_description: string; answers: { text: string; correct: boolean }[] }[];
      image_url: string | null;
    }[];
  }[];
};

export type DeployedTemplate = {
  key: string;
  name: Record<string, string> | null;
  exercises_per_day: number;
  includes_functional_test: boolean;
  health_form_type: string;
  duration_weeks: number | null;
  group_key: string | null;
  retired: boolean;
  exercises: {
    exercise_name: string;
    days: number[] | null;
    weeks: number[] | null;
    recommended_level: number;
    recommended_sets: number | null;
    recommended_reps: string | null;
    level_locked: boolean;
    pinned: boolean;
  }[];
  lessons: { lesson_name: string; position: number }[];
  sustain_exercises: { exercise_name: string }[];
};

export type CatalogueEntry = {
  kind: CatalogueKind;
  name: string;
  exercise: DeployedExercise | null;
  lesson: DeployedLesson | null;
  template: DeployedTemplate | null;
};

export const deployedCatalogueEntryKey = (kind: CatalogueKind, name: string, group: EnvGroup) =>
  ["catalogue-deployed", kind, name, group] as const;

export const deployableCatalogueEntryKey = (kind: CatalogueKind, name: string) =>
  ["catalogue-deployable", kind, name] as const;

const errorStatus = (error: unknown): number | undefined => {
  try {
    return (JSON.parse((error as Error).message) as APIError).status;
  } catch (err) {
    return undefined;
  }
};

export const useGetDeployedCatalogueEntry = (kind: CatalogueKind, name: string, group: EnvGroup, enabled: boolean) =>
  useQuery<CatalogueEntry | null>(
    deployedCatalogueEntryKey(kind, name, group),
    async () => {
      try {
        // @ts-ignore — v2 endpoint, not in OpenAPI types
        return await api.parse(`GET /catalogue/${kind}/${encodeURIComponent(name)}/deployments/${group}`, {
          root: supportApiBase,
        });
      } catch (error) {
        if (errorStatus(error) === 404) return null;
        throw error;
      }
    },
    { enabled, retry: false, refetchOnMount: "always" }
  );

export const useGetDeployableCatalogueEntry = (kind: CatalogueKind, name: string, enabled: boolean) =>
  useQuery<CatalogueEntry | null>(
    deployableCatalogueEntryKey(kind, name),
    async () =>
      // @ts-ignore — v2 endpoint, not in OpenAPI types
      api.parse(`GET /catalogue/${kind}/${encodeURIComponent(name)}/deployable`, { root: supportApiBase }),
    { enabled, retry: false, refetchOnMount: "always" }
  );

export const useRevertCatalogueEntryToDeployed = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ kind, name, group }: CatalogueTarget): Promise<void> => {
      await api.call(`/catalogue/${kind}/${encodeURIComponent(name)}/deployments/${group}/revert`, {
        root: supportApiBase,
        method: "POST",
      });
    },
    {
      onSuccess: (_result, target) => {
        invalidateDeployed(queryClient, target);
        queryClient.invalidateQueries(deployableCatalogueEntryKey(target.kind, target.name));
        queryClient.invalidateQueries(queryKeys.templates());
      },
    }
  );
};
